import { useEffect, useState, useContext } from "react";

import useAxios from "../../utils/useAxios";
import { resObj } from "../../utils/constants";
import { useForm } from "../../utils/useForm";
import PermissionContext from "../../context/PermissionContext";
import DrawerContext from "../../context/DrawerContext";
import AutoLogout from "../../utils/useAutoLogout";

import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import AddIcon from '@mui/icons-material/Add';

import SideDrawer from "../../components/layout/SideDrawer";
import Loader from "../../components/layout/Loader";
import SuccessAlert from "../../components/layout/SuccessAlert";
import NoData from "../../components/layout/NoData";
import ErrorModal from "../../components/layout/ErrorModal";
import InputModalForm from "../../components/layout/InputModalForm";
import TopBarWithBack from "../../components/layout/TopBarWithBack";
import FileColumnsTable from "../../components/user_mngmnt/FileColumnsTable";
import DeleteModal from "../../components/layout/DeleteModal";

const FileTypeEditPage = () => {
  const api = useAxios();
  const { permisionsList, checkUserPermission } = useContext(PermissionContext);
  const { drawerValue, setDrawerValue } = useContext(DrawerContext);

  const backURL = '/file-types';
  const docUrIArr = window.location.href.split('/');
  const fileTypeId = docUrIArr[docUrIArr.length - 1];
  const deleteModalTitle = 'Column Delete';
  const deleteModalMsg = 'Are you sure you want to delete (Column will be lost)?';

  useEffect(() => {
    if(permisionsList.length > 0) checkUserPermission(['Admin-Manage Organisation']);
  }, [permisionsList]);

  const initialValues = {
    fileType: '',
    columns: [],
  };
  const {values, setValues} = useForm(initialValues);
  const [columnName, setColumnName] = useState('');
  const [apiRes, setApiRes] = useState(resObj);
  const [columnId, setColumnId] = useState('');
  const [modalTitle, setModalTitle] = useState('Column Create');
  const [showModal, setShowModal] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  useEffect(() => {
    getFileTypeDetails();
  }, []);

  const getFileTypeDetails = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get('/api/user/organisation/file-type/details/get', {
        params: { file_type_id: fileTypeId }
      });
      
      if (response.status === 200) {        
        setValues({
          ...values,
          fileType: response.data.file_type,
          columns: response.data.columns,
        });
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'File Type Details Get',
        loading: false,
      });
    }
  };

  const validate = () => {
    const errors = {};
    if (!columnName) {
      errors.columnName = 'Column name is required!';
    }
    return errors;
  };

  useEffect(() => {
    if (Object.keys(formErrors).length === 0 && isSubmit) {
      let url = '/api/user/organisation/file-type/column/create';
      let data = {
        column_name: columnName,
      };
      if (columnId === '') {
        data.file_type_id = fileTypeId;
      } else {
        url = '/api/user/organisation/file-type/column/edit'
        data.column_id = columnId;
      }
      columnCreateOrUpdate(url, data);
    }
  }, [formErrors]);

  const handleSubmit = e => {
    e.preventDefault();
    setFormErrors(validate());
    setIsSubmit(true);
  };

  const handleColumnDelete = (id) => {
    setColumnId(id);
    setDeleteModalOpen(true);
  };

  const handleColumnEdit = (id, colName) => {
    setModalTitle('Column Edit');
    setColumnId(id);
    setColumnName(colName);
    setShowModal(true);
  };

  const deleteColumn = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.delete(`/api/user/organisation/file-type/column/delete/${columnId}`);

      if (response.status === 200) {
        setDeleteModalOpen(false);
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: 'Column deleted successfully',
          loading: false,
        });
      }
      getFileTypeDetails();
    } catch (err) {
      setDeleteModalOpen(false);
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Column Delete',
        loading: false,
      });
    }
  };

  const handleColumnCreate = () => {
    setModalTitle('Column Create');
    setColumnId('');
    setShowModal(true);
  };

  const columnCreateOrUpdate = async (url, data) => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.post(url, data);

      if (response.status === 200) {
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: response.data,
          loading: false,
        });
        setShowModal(false);
        setColumnName('');
        getFileTypeDetails();
      }
    } catch (err) {
      const errMsg = JSON.stringify(err.response.data).replace(/[{}[\]"']/g, '');
      if (errMsg === 'Column already exists.') {
        setFormErrors({ 
          columnName: 'Column already exists.' 
        });
      }
      else {
        setShowModal(false);
        setColumnName('');
        setApiRes({
          ...apiRes,
          axiosError: true,
          errMsg: errMsg,
          errHeading: modalTitle,
          loading: false,
        });
      }
    }
  };

  return(
    <AutoLogout>
      <SideDrawer drawerValue={drawerValue} setDrawerValue={setDrawerValue} />
      {apiRes.loading && <Loader />}
      <div className="mainContainer" style={{ marginLeft: drawerValue.drawerWidth }}>
        {apiRes.showAlert && <SuccessAlert apiRes={apiRes} setApiRes={setApiRes} />}
        <TopBarWithBack title={values.fileType} backURL={backURL} />

        <div 
          style={{ 
            display: 'flex', 
            justifyContent: 'space-between'
          }}
        >
          <Typography 
            component="h5" 
            variant="h5" 
            color={'text.secondary'}
            sx={{ margin: 1.5 }}
          >
            <strong>Columns ({values.columns.length})</strong>
          </Typography>

          <Button 
            variant="contained" 
            color="success" 
            startIcon={<AddIcon />}
            sx={{ 
              marginTop: 'auto', 
              marginBottom: 'auto'
            }}
            onClick={handleColumnCreate}
          >
            Create
          </Button>
        </div>

        <FileColumnsTable 
          rows={values.columns} 
          handleEdit={handleColumnEdit} 
          handleDelete={handleColumnDelete} 
          excludeKeys={['id']} 
        />

        <InputModalForm 
          title={modalTitle} 
          label={'Column Name'} 
          show={showModal} 
          setShow={setShowModal} 
          value={columnName} 
          setValue={setColumnName} 
          formErrors={formErrors} 
          errorKey={'columnName'} 
          handleSubmit={handleSubmit} 
        />

        <DeleteModal
          title={deleteModalTitle} 
          message={deleteModalMsg} 
          open={deleteModalOpen}
          setOpen={setDeleteModalOpen}
          onDelete={deleteColumn}
        />

        {values.columns.length === 0  && <NoData /> }

        <ErrorModal apiRes={apiRes} setApiRes={setApiRes} />
      </div>
    </AutoLogout>
  );
};

export default FileTypeEditPage;